import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, addToCart } = useCart();
  const { user } = useAuth();
  const cartTotal = getCartTotal();
  const walletBalance = user?.wallet?.depositWallet || 0;
  const canPayWithWallet = walletBalance >= cartTotal;

  // Debug: Log cart state
  console.log('Cart state:', { cart, cartTotal, user });

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-5 py-10">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-secondary mb-2">Shopping Cart</h1>
          <p className="text-gray-500">Your cart is empty</p>
          <p className="text-xs text-gray-400 mt-2">Debug: Cart length = {cart.length}</p>
        </div>

        <div className="card bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-6xl mb-4">🛒</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Start shopping to add items to your cart.</p>
          
          {/* Debug: Test Add to Cart Button */}
          <div className="mb-4">
            <button 
              onClick={() => {
                const testProduct = {
                  _id: 'test-' + Date.now(),
                  name: 'Test Product',
                  price: 100,
                  category: 'TEST'
                };
                addToCart(testProduct, 1);
                console.log('Test product added to cart');
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded mb-2"
            >
              Debug: Add Test Product
            </button>
          </div>
          
          <Link to="/products">
            <button className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-semibold">
              Continue Shopping
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary mb-2">Shopping Cart</h1>
        <p className="text-gray-500">{cart.length} item(s) in cart</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2">
          <div className="card bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Product</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Price</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Quantity</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Total</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cart.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {item.image && (
                          <img 
                            src={item.image} 
                            alt={item.name} 
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div>
                          <div className="font-semibold text-secondary line-clamp-1">{item.name}</div>
                          <div className="text-xs text-gray-500 mt-1">{item.category?.replace(/_/g, ' ')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-sm font-semibold text-primary">Rs. {item.price?.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => updateQuantity(item._id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-600 font-semibold"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            if (val > 0) updateQuantity(item._id, val);
                          }}
                          className="w-12 text-center border border-gray-300 rounded-lg py-1 text-sm"
                        />
                        <button
                          onClick={() => updateQuantity(item._id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-600 font-semibold"
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-sm font-bold text-secondary">
                        Rs. {(item.price * item.quantity).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => removeFromCart(item._id)}
                        className="text-danger hover:bg-red-50 px-3 py-2 rounded-lg transition-colors font-semibold text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex gap-4">
            <Link to="/products">
              <button className="px-6 py-3 bg-gray-200 text-secondary rounded-lg hover:bg-gray-300 transition-colors font-semibold">
                Continue Shopping
              </button>
            </Link>
            <button
              onClick={() => {
                if (cartTotal < 310) {
                  alert('Minimum purchase amount is ₹310. Please add more items to your cart.');
                } else {
                  // Navigate to checkout
                  window.location.href = '/checkout';
                }
              }}
              className={`px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-semibold ${
                cartTotal < 310 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              disabled={cart.length === 0}
            >
              {cartTotal < 310 ? 'Add More Items (Min: ₹310)' : 'Proceed to Checkout'}
            </button>
          </div>
        </div>

        {/* Cart Summary */}
        <div className="lg:col-span-1">
          <div className="card bg-white rounded-lg shadow-md p-6 sticky top-24">
            <h3 className="text-xl font-bold text-secondary mb-6">Order Summary</h3>
            
            {/* Wallet Balance */}
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-green-800">Deposit Wallet:</span>
                <span className="text-lg font-bold text-green-600">Rs. {walletBalance.toLocaleString()}</span>
              </div>
              {!canPayWithWallet && cartTotal > 0 && (
                <div className="mt-2 text-xs text-red-600">
                  Need Rs. {(cartTotal - walletBalance).toLocaleString()} more to pay with wallet
                </div>
              )}
            </div>
            
            <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span className="font-semibold">Rs. {cartTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Shipping:</span>
                <span className="font-semibold text-accent">Free</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Tax (0%):</span>
                <span className="font-semibold">Rs. 0</span>
              </div>
            </div>

            <div className="flex justify-between items-center mb-8">
              <span className="text-lg font-bold text-secondary">Total:</span>
              <span className="text-2xl font-bold text-primary">Rs. {cartTotal.toLocaleString()}</span>
            </div>

            {cartTotal < 279 && (
              <div className="text-sm text-red-600 font-semibold mb-4">
                Minimum order value is ₹279. Please add more items.
              </div>
            )}

            <button
              onClick={() => {
                if (cartTotal < 310) {
                  alert('Minimum purchase amount is ₹310. Please add more items to your cart.');
                } else {
                  // Navigate to checkout
                  window.location.href = '/checkout';
                }
              }}
              className={`w-full px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-bold text-lg ${
                cartTotal < 310 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
              }`}
              disabled={cart.length === 0}
            >
              {cartTotal < 310 ? 'Add More Items (Min: ₹310)' : 'Proceed to Checkout'}
            </button>

            <div className="mt-4 space-y-2">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800">
                  ✓ Free shipping on all orders
                </p>
                <p className="text-xs text-blue-800 mt-2">
                  ✓ Earn commission on this purchase
                </p>
              </div>
              {canPayWithWallet && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-xs text-green-800 font-semibold">
                    ✓ You can pay using your deposit wallet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;